import type { CartLine } from "@/store/cart";
import type { OrderLineInput } from "@/api/online-ordering-service/model";
import type { UiMenuItem } from "@/hooks/useMenu";
import type { TillPricedLine } from "@/lib/till-pricing";

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Single source of truth for turning a local cart line into the BFF's
 * `OrderLineInput` shape.
 *
 * Three callers need the exact same payload:
 *  - checkout's `orderApi.create` (all lines at once, on a brand-new order)
 *  - checkout's dine-in append path, `orderApi.addLine` (one line at a time,
 *    onto an already-open table order)
 *  - ItemDetailScreen's direct append-to-open-order path
 *
 * Keeping one builder means an append can never drift from a create — most
 * importantly `pricingSummary` and the FR-019 recommendation attribution,
 * which the BFF reads off the line itself.
 *
 * PRICING: `unitPrice` is the RAW catalog base price and never includes
 * variants or modifiers, which ride in `variantAmount` / `modifierAmount`.
 * See `lib/order-line-pricing-contract/SPEC.md`. This matters because pos_v2
 * recomputes a line as `unitPrice + variantAmount + modifierAmount`; when we
 * sent a resolved `unitPrice` with zeroed adjustments, a cashier opening the
 * order on the POS doubled every line that had a variant (tenant 525).
 */
export function cartLineToOrderLineInput(line: CartLine, indexId: number, priced?: TillPricedLine | null): OrderLineInput {
  // Per-unit adjustments, read off the line's own selections — the same source
  // pos_v2 re-derives them from.
  const variantPerUnit = line.selectedVariants.reduce(
    (sum, v) => sum + (v.priceAdjustment ?? 0),
    0,
  );
  const modifierPerUnit = line.selectedModifiers.reduce(
    (sum, m) => sum + (m.unitPrice ?? 0) * (m.quantity ?? 1),
    0,
  );

  // `basePrice` is absent on carts persisted before it existed; derive it from
  // the resolved price, which is base + variants + modifiers by construction.
  // Clamped at 0 so a malformed stale line can never produce a negative base.
  const rawBase =
    line.basePrice ?? Math.max(0, round2(line.price - variantPerUnit - modifierPerUnit));

  const baseAmount = round2(rawBase * line.qty);
  const variantAmount = round2(variantPerUnit * line.qty);
  const modifierAmount = round2(modifierPerUnit * line.qty);
  const subtotalAmount = round2(baseAmount + variantAmount + modifierAmount);

  return {
    indexId,
    itemId: line.itemId,
    itemName: { en: line.name },
    categoryId: line.categoryId,
    categoryName: line.categoryNameRaw
      ? {
          en: line.categoryNameRaw.en || line.categoryNameRaw.ar,
          ar: line.categoryNameRaw.ar || line.categoryNameRaw.en,
        }
      : { en: line.categoryName },
    quantity: line.qty,
    unitPrice: rawBase,
    notes: line.notes || null,
    imageUrl: line.imageUrl || null,
    selectedModifiers: line.selectedModifiers.map((m) => ({
      modifierGroupId: m.modifierGroupId,
      modifierGroupName: m.modifierGroupName,
      modifierOptionId: m.modifierOptionId,
      modifierOptionName: m.modifierOptionName,
      quantity: m.quantity,
      unitPrice: m.unitPrice,
      imageUrl: m.imageUrl || null,
    })),
    selectedVariants: line.selectedVariants.map((v) => ({
      variantGroupId: v.variantGroupId,
      variantGroupName: v.variantGroupName,
      variantOptionId: v.variantOptionId,
      variantOptionName: v.variantOptionName,
      priceAdjustment: v.priceAdjustment,
      imageUrl: v.imageUrl || null,
    })),
    // Priced by the till port (checkout): every money field is the till's.
    // Otherwise (dine-in append, item screen): the line's own decomposition.
    pricingSummary: priced
      ? tillLinePricingSummary(priced)
      : {
          baseAmount,
          variantAmount,
          modifierAmount,
          subtotalAmount,
          lineTotalBeforeTax: subtotalAmount,
          totalAmount: subtotalAmount,
        },
    ...(priced ? { taxInfo: tillLineTaxInfo(priced) } : {}),
    // Recommendation attribution (FR-019 Phase 1) — carried on the cart
    // line when the item was added via a rec strip tap; the BFF's
    // OrderLineCreateRequest accepts these five fields.
    ...(line.recommendationId
      ? {
          recommendationId: line.recommendationId,
          recommendationSource: line.recommendationSource ?? null,
          recommendationSurface: line.recommendationSurface ?? "online",
          recommendationReasonCode: line.recommendationReasonCode ?? null,
          recommendationRank: line.recommendationRank ?? null,
        }
      : {}),
  };
}

/** The till's line amounts in the order-line `pricingSummary` shape. */
export function tillLinePricingSummary(priced: TillPricedLine): OrderLineInput["pricingSummary"] {
  return {
    baseAmount: priced.baseAmount,
    variantAmount: priced.variantAmount,
    modifierAmount: priced.modifierAmount,
    subtotalAmount: priced.subtotalAmount,
    discountAmount: priced.discountAmount,
    promotionAmount: priced.promotionAmount,
    proratedOrderPromotionAmount: priced.orderPromotionAmount,
    proratedOrderDiscountAmount: priced.orderDiscountAmount,
    lineTotalBeforeTax: priced.lineTotalBeforeTax,
    taxAmount: priced.taxAmount,
    totalAmount: priced.totalAmount,
  };
}

function tillLineTaxInfo(priced: TillPricedLine): NonNullable<OrderLineInput["taxInfo"]> {
  return priced.taxInfo.map((t) => ({
    taxId: t.taxId ?? "",
    ratePercent: t.rate,
    taxableAmount: t.taxableAmount,
    calculatedAmount: t.calculatedAmount,
    isIncludedInPrice: t.isIncludedInPrice,
  })) as NonNullable<OrderLineInput["taxInfo"]>;
}

/**
 * A free-item line the till granted (priceCart `lines` entry with
 * `grantedByPromotionId`), in the order-create shape. The till builds it from
 * the menu entry: base price, the granted variant, no modifiers.
 */
export function grantedLineToOrderLineInput(
  priced: TillPricedLine,
  menuItem: UiMenuItem | null | undefined,
  indexId: number,
): OrderLineInput {
  const raw = (menuItem?.raw ?? {}) as {
    variantGroups?: Array<{ id: string; name?: unknown; options?: Array<{ id: string; name?: unknown; price?: { amount?: number } }> }>;
  };
  const selectedVariants = priced.variantOptionIds.flatMap((id) => {
    for (const g of raw.variantGroups ?? []) {
      const o = g.options?.find((x) => x.id === id);
      if (o) {
        return [{
          variantGroupId: g.id,
          variantGroupName: g.name as never,
          variantOptionId: o.id,
          variantOptionName: o.name as never,
          priceAdjustment: o.price?.amount ?? 0,
        }];
      }
    }
    return [];
  });
  return {
    indexId,
    lineId: `grant-${priced.grantedByPromotionId}-${priced.itemId}-${priced.variantOptionIds[0] ?? "default"}`,
    itemId: priced.itemId,
    itemName: { en: menuItem?.name || priced.itemId },
    quantity: priced.quantity,
    unitPrice: priced.unitPrice,
    selectedVariants,
    selectedModifiers: [],
    pricingSummary: tillLinePricingSummary(priced),
    taxInfo: tillLineTaxInfo(priced),
    grantedByPromotionId: priced.grantedByPromotionId,
    notes: null,
  } as OrderLineInput;
}
